import { module as BaseModule } from "modujs"

let googleMapsPromise = null

const loadGoogleMaps = (apiKey) => {
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google)
  }

  if (!apiKey) {
    return Promise.reject(new Error("Missing Google Maps API key"))
  }

  if (!googleMapsPromise) {
    googleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = `farmformMapInit_${Date.now()}`
      window[callbackName] = () => {
        resolve(window.google)
        delete window[callbackName]
      }

      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async&libraries=marker`
      script.async = true
      script.defer = true
      script.onerror = () => {
        googleMapsPromise = null
        delete window[callbackName]
        reject(new Error("Google Maps failed to load"))
      }
      document.head.appendChild(script)
    })
  }

  return googleMapsPromise
}

export default class extends BaseModule {
  init() {
    const { mapLat, mapLng, mapKey, mapZoom, mapLink, mapMarker } = this.el.dataset

    this.state = {
      lat: parseFloat(mapLat),
      lng: parseFloat(mapLng),
      apiKey: mapKey,
      zoom: mapZoom ? parseInt(mapZoom, 10) : 15,
      link: mapLink,
      marker: mapMarker,
    }

    if (Number.isNaN(this.state.lat) || Number.isNaN(this.state.lng)) {
      console.warn("[Map] Missing coordinates")
      return
    }

    loadGoogleMaps(this.state.apiKey)
      .then((google) => {
        this.google = google
        this.renderMap()
      })
      .catch((error) => {
        console.error("[Map] Unable to initialize Google Maps", error)
      })
  }

  renderMap() {
    if (!this.google) return

    const position = { lat: this.state.lat, lng: this.state.lng }
    const mapOptions = {
      center: position,
      zoom: this.state.zoom,
      disableDefaultUI: true,
      gestureHandling: "cooperative",
      mapId: "5596bc1584d369c49396b7fd",
    }

    this.map = new this.google.maps.Map(this.el, mapOptions)

    this.setupControls()

    const markerContent = this.buildMarkerContent()
    const markerOptions = {
      map: this.map,
      position,
      gmpClickable: true,
    }
    if (markerContent) {
      markerOptions.content = markerContent
    }

    // Always use AdvancedMarkerElement when available (preferred and non-deprecated)
    if (this.google.maps.marker?.AdvancedMarkerElement) {
      try {
        this.marker = new this.google.maps.marker.AdvancedMarkerElement(markerOptions)
      } catch (error) {
        console.warn("[Map] Failed to create AdvancedMarkerElement, falling back to classic Marker", error)
        this.marker = new this.google.maps.Marker({
          map: this.map,
          position,
          clickable: true,
          icon: this.getMarkerIcon(),
        })
      }
    } else {
      // Fall back to classic Marker if AdvancedMarkerElement is not available
      this.marker = new this.google.maps.Marker({
        map: this.map,
        position,
        clickable: true,
        icon: this.getMarkerIcon(),
      })
    }
  
    if (this.state.link) {
      this.google.maps.event.addListener(this.map, "tilesloaded", () => {
        this.el.classList.add("is-ready")
      })

      // Open map link when marker is clicked
      const markerClickEvent = this.marker.addListener
        ? this.marker.addListener("gmp-click", () => {
            window.open(this.state.link, "_blank", "noopener")
          })
        : this.google.maps.event.addListener(this.marker, "click", () => {
            window.open(this.state.link, "_blank", "noopener")
          })

      this.markerClickListener = markerClickEvent
    }
  }

  getMarkerIcon() {
    // Only used for classic Marker fallback
    if (!this.state.marker || !this.google) return null
    return {
      url: this.state.marker,
      scaledSize: new this.google.maps.Size(57, 64),
      anchor: new this.google.maps.Point(24, 24),
    }
  }

  buildMarkerContent() {
    if (!this.state.marker) return null
    const img = document.createElement("img")
    img.src = this.state.marker
    img.alt = this.el.dataset.mapTitle || "Location pin"
    img.width = 57
    img.height = 64
    img.decoding = "async"
    img.loading = "lazy"
    img.className = "map-marker"
    return img
  }


  destroy() {
    if (this.markerClickListener?.remove) {
      this.markerClickListener.remove()
    }

    if (this.marker) {
      if (this.marker.map !== undefined) {
        this.marker.map = null
      } else {
        this.google?.maps.event.clearInstanceListeners(this.marker)
        this.marker.setMap(null)
      }
    }

    this.teardownControls()

    this.map = null
    this.marker = null
    this.markerClickListener = null
  }

  setupControls() {
    this.rootEl = this.el.closest("[data-map-root]") || this.el.parentElement
    if (!this.rootEl) return

    this.zoomInButton = this.rootEl.querySelector("[data-map-zoom-in]")
    this.zoomOutButton = this.rootEl.querySelector("[data-map-zoom-out]")

    if (this.zoomInButton) {
      this.onZoomIn = this.handleZoomIn.bind(this)
      this.zoomInButton.addEventListener("click", this.onZoomIn)
    }

    if (this.zoomOutButton) {
      this.onZoomOut = this.handleZoomOut.bind(this)
      this.zoomOutButton.addEventListener("click", this.onZoomOut)
    }
  }

  teardownControls() {
    if (this.zoomInButton && this.onZoomIn) {
      this.zoomInButton.removeEventListener("click", this.onZoomIn)
    }

    if (this.zoomOutButton && this.onZoomOut) {
      this.zoomOutButton.removeEventListener("click", this.onZoomOut)
    }
  }

  handleZoomIn(event) {
    event.preventDefault()
    this.adjustZoom(1)
  }

  handleZoomOut(event) {
    event.preventDefault()
    this.adjustZoom(-1)
  }

  adjustZoom(delta) {
    if (!this.map || typeof this.map.getZoom !== "function") return
    const current = this.map.getZoom() || this.state.zoom
    const next = Math.min(21, Math.max(4, current + delta))
    if (Number.isFinite(next)) {
      this.map.setZoom(next)
    }
  }
}
